import { createElement } from 'react';
import ky, { type KyRequest, type KyResponse, type NormalizedOptions } from 'ky';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle } from '@tabler/icons-react';
import { type InternalApiHttpAuthRefreshResponse } from '~/generated/authBackend/model';
import { getPostAuthRefreshUrl } from '~/generated/authBackend/auth';
import { isObject } from '../lib/utils/isObject';
import { xHeaders } from '../constants';
import { ErrorNotificationMessage } from '../ui/ErrorNotificationMessage';
import { getGlobalArg } from '../lib/getGlobalArg';
import { getTokenProvider } from './token-provider';

const applyHeaders = () => async (request: KyRequest) => {
    const tokenProvider = getTokenProvider();

    if (tokenProvider) {
        const accessToken = await tokenProvider.getAccessToken();

        if (accessToken) {
            request.headers.set('Authorization', `Bearer ${accessToken}`);
        }
    }
};

const refreshAuth = async (request: KyRequest, response: KyResponse) => {
    if (response.status === 401) {
        const tokenProvider = getTokenProvider();

        if (!tokenProvider) {
            return response;
        }

        const accessToken = await tokenProvider.getAccessToken();

        if (!accessToken) {
            return response;
        }

        try {
            const res = await ky.post<InternalApiHttpAuthRefreshResponse>(
                `${getGlobalArg('PUBLIC_AUTH_API_URL')}${getPostAuthRefreshUrl()}`,
                {
                    credentials: 'include',
                },
            );

            const { accessToken } = await res.json();

            if (accessToken) {
                await tokenProvider.updateAccessToken(accessToken);

                request.headers.set('Authorization', `Bearer ${accessToken}`);

                return ky(request);
            }
        } catch {
            await tokenProvider.updateAccessToken(null);
            tokenProvider.onUnauthorized?.();
        }
    }

    return response;
};

const showErrorNotification = async (response: KyResponse) => {
    if (response.status >= 400) {
        const traceId = response.headers.get(xHeaders['trace-id']);

        let errorMessage = 'Произошла ошибка при выполнении запроса';
        let errorTitle = 'Ошибка';

        try {
            const res = response.clone();
            const body = await res.json();

            if (body && isObject(body)) {
                errorMessage = body.message || body.error || errorMessage;

                if (response.status === 400) {
                    errorTitle = 'Неверный запрос';
                } else if (response.status === 401) {
                    errorTitle = 'Ошибка авторизации';
                } else if (response.status === 403) {
                    errorTitle = 'Нет доступа';
                } else if (response.status >= 500) {
                    errorTitle = 'Ошибка сервера';
                }
            }
        } catch {}

        notifications.show({
            title: errorTitle,
            message: createElement(ErrorNotificationMessage, {
                message: errorMessage,
                traceId,
            }),
            color: 'red',
            icon: createElement(IconAlertCircle, {
                size: 16,
            }),
            autoClose: traceId ? false : 5000,
        });
    }

    return response;
};

export const shouldSkipErrorNotification = (context: Record<string, unknown> | undefined, status: number) => {
    if (context?.skipErrorNotification === true) {
        return true;
    }

    const statuses = context?.skipErrorNotificationStatuses;

    return Array.isArray(statuses) && statuses.includes(status);
};

export const request = ky.create({
    credentials: 'include',
    timeout: 30000,
    prefixUrl: '',
    throwHttpErrors: true,
    headers: {
        'Content-Type': 'application/json',
    },
    hooks: {
        beforeRequest: [applyHeaders()],
        afterResponse: [
            async (request, _options, response) => {
                return refreshAuth(request, response);
            },
            async (_request: KyRequest, options: NormalizedOptions, response: KyResponse) => {
                if (shouldSkipErrorNotification(options.context, response.status)) {
                    return response;
                }

                return showErrorNotification(response);
            },
        ],
    },
});

export const authRequest = request.extend({
    prefixUrl: getGlobalArg('PUBLIC_AUTH_API_URL'),
    hooks: {
        beforeRequest: [applyHeaders()],
    },
});

export const banksRequest = ky.create({
    credentials: 'omit',
    timeout: 60000,
    prefixUrl: getGlobalArg('PUBLIC_BANKS_API_URL'),
    throwHttpErrors: true,
});

export const payoutRequest = request.extend({
    prefixUrl: getGlobalArg('PUBLIC_PAYOUT_API_URL'),
    hooks: {
        beforeRequest: [applyHeaders()],
    },
});

export const payinRequest = request.extend({
    prefixUrl: getGlobalArg('PUBLIC_PAYIN_API_URL'),
    hooks: {
        beforeRequest: [applyHeaders()],
    },
});
